import React, { useEffect, useState } from 'react';
import { useSEO } from '../hooks/useSEO';
import PageHero from '../components/PageHero';
import { AUDIT_BOARD, ADVISORY_BOARD } from '../data/mock';
import { ShieldCheck, Users, UserPlus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { EditRegion } from '../components/AdminEditBar';
import { InlineText } from '../components/InlineEditable';
import { api } from '../lib/api';

export default function Boards() {
  useSEO('boards');
  const [dbMembers, setDbMembers] = useState([]);
  useEffect(() => {
    api.publicContent('board_members').then(rows => setDbMembers(rows || [])).catch(() => {});
  }, []);

  const auditMembers = dbMembers
    .filter(r => r?.data?.group === 'audit')
    .map(r => ({ id: r.id, name: r.data?.name, role: r.data?.role }));
  const advisoryMembers = dbMembers
    .filter(r => r?.data?.group === 'advisory')
    .map(r => ({ id: r.id, name: r.data?.name, role: r.data?.role }));

  const finalAudit = auditMembers.length > 0 ? auditMembers : AUDIT_BOARD;
  const finalAdvisory = advisoryMembers.length > 0 ? advisoryMembers : ADVISORY_BOARD;
  const auditFromDb = auditMembers.length > 0;
  const advisoryFromDb = advisoryMembers.length > 0;

  return (
    <EditRegion label="Yönetim Kurulu" to="/admin/board">
      <>
      <PageHero slug="boards"
        kicker="Kurullarımız"
        title={<>Karar mekanizmamızın <span className="italic text-coral-200">kurulları</span>.</>}
        text="Vakfımızın şeffaf yönetim yapısı, üç ayrı kurulun denetim ve danışmanlığıyla korunur."
      />
      <section className="bg-cream">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-20 md:py-28 grid md:grid-cols-2 gap-10">
          <BoardCard
            icon={ShieldCheck}
            dark
            kickerKey="board_audit_kicker" kickerFallback="Denetim Kurulu"
            titleKey="board_audit_title" titleFallback="Finansal ve operasyonel denetim"
            textKey="board_audit_text" textFallback="Her yıl bağımsız denetim yapar; bagışcılarımıza yıllık şeffaf rapor yayımlar."
            members={finalAudit}
            fromDb={auditFromDb}
            groupKey="audit"
            fallbackKeyPrefix="audit"
          />
          <BoardCard
            icon={Users}
            kickerKey="board_advisory_kicker" kickerFallback="Üst Danışma Kurulu"
            titleKey="board_advisory_title" titleFallback="Stratejik rehberlik"
            textKey="board_advisory_text" textFallback="Eğitim politikaları, sivil toplum ve kurumsal iletişim alanında uzmanlardan oluşur."
            members={finalAdvisory}
            fromDb={advisoryFromDb}
            groupKey="advisory"
            fallbackKeyPrefix="advisory"
          />
        </div>
      </section>
    </>
    </EditRegion>
  );
}

function BoardCard({ icon: Icon, dark = false, kickerKey, kickerFallback, titleKey, titleFallback, textKey, textFallback, members, fromDb, groupKey, fallbackKeyPrefix }) {
  const cardCls = dark
    ? 'bg-ink-600 text-cream'
    : 'bg-white text-ink-600 border border-ink-100';
  return (
    <div className={`rounded-3xl p-10 ${cardCls}`}>
      <div className={`w-14 h-14 rounded-2xl grid place-items-center ${dark ? 'bg-cream/10 text-gold' : 'bg-ink-50 text-ink-600'}`}><Icon className="w-6 h-6" /></div>
      <InlineText
        settingKey={kickerKey}
        fallback={kickerFallback}
        as="div"
        className={`mt-6 text-[10px] uppercase tracking-widest font-semibold ${dark ? 'text-gold' : 'text-coral-300'}`}
      />
      <InlineText
        settingKey={titleKey}
        fallback={titleFallback}
        as="h3"
        multiline
        className="font-display text-3xl md:text-4xl mt-2 leading-tight"
      />
      <InlineText
        settingKey={textKey}
        fallback={textFallback}
        as="p"
        multiline
        className={`mt-3 text-sm leading-relaxed ${dark ? 'text-cream/75' : 'text-ink-500'}`}
      />
      <div className={`h-px my-8 ${dark ? 'bg-cream/10' : 'bg-ink-100'}`} />

      <EditRegion label={`${kickerFallback} Üyelerini Yönet`} to="/admin/board">
      <ul className="space-y-3" data-testid={`board-members-${groupKey}`}>
        {members.map((m, i) => (
          <li key={m.id || i} className="flex items-center justify-between text-sm gap-4">
            {fromDb ? (
              <>
                <span className="font-medium">{m.name}</span>
                <span className={`text-xs text-right ${dark ? 'text-cream/60' : 'text-ink-400'}`}>{m.role}</span>
              </>
            ) : (
              <>
                <InlineText
                  settingKey={`board_${fallbackKeyPrefix}_${i + 1}_name`}
                  fallback={m.name}
                  as="span"
                  className="font-medium"
                />
                <InlineText
                  settingKey={`board_${fallbackKeyPrefix}_${i + 1}_role`}
                  fallback={m.role}
                  as="span"
                  className={`text-xs text-right ${dark ? 'text-cream/60' : 'text-ink-400'}`}
                />
              </>
            )}
          </li>
        ))}
      </ul>
      </EditRegion>

      {/* Admin add-member CTA — only visible in edit mode via the yekuv-edit-on class */}
      <div className="mt-5 yekuv-admin-only" style={{ display: 'none' }}>
        <Link
          to={`/admin/board?add=1&group=${groupKey}`}
          data-testid={`add-member-${groupKey}`}
          className={`inline-flex items-center gap-2 rounded-full px-4 h-10 text-xs font-semibold transition-colors ${dark ? 'bg-gold text-ink-700 hover:bg-gold-400' : 'bg-ink-600 text-cream hover:bg-ink-500'}`}
        >
          <UserPlus className="w-3.5 h-3.5" /> Yeni Üye Ekle
        </Link>
      </div>
    </div>
  );
}
